/**
 * OOP User Model
 * Core user class with encapsulated behavior and computed properties
 */

import { MerchantProfile } from '@/types/api/merchant';

export interface UserBalances {
  [currency: string]: number;
}

export interface UserProfile {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  avatar?: string;
  bio?: string;
  dateOfBirth?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
}

export interface OtherProfiles {
  merchant?: MerchantProfile;
  bank?: any;
  developer?: any;
}

export interface UserData {
  _id: string;
  username: string;
  email?: string;
  profile?: UserProfile;
  roles?: string[];
  balances: UserBalances;
  otherProfiles?: OtherProfiles;
  hasAdminAccess?: boolean;
  isCustomerSupport?: boolean;
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
  isGuest?: boolean;
}

/**
 * User class with OOP principles
 */
export class User {
  private _data: UserData;
  private _cachedComputedProperties: Map<string, any> = new Map();

  constructor(data: UserData) {
    this._data = { ...data };
    this._cachedComputedProperties.clear();
  }

  // ===== Core Properties =====
  
  get id(): string {
    return this._data._id;
  }

  get username(): string {
    return this._data.username;
  }

  get email(): string | undefined {
    return this._data.email;
  }

  get profile(): UserProfile {
    return this._data.profile || {};
  }

  get roles(): string[] {
    return this._data.roles || [];
  }

  get balances(): UserBalances {
    return this._data.balances || {};
  }

  get otherProfiles(): OtherProfiles {
    return this._data.otherProfiles || {};
  }

  get isGuest(): boolean {
    return this._data.isGuest || false;
  }

  // ===== Computed Properties with Caching =====

  get fullName(): string {
    const cached = this._cachedComputedProperties.get('fullName');
    if (cached !== undefined) return cached;

    const firstName = this.profile.firstName || '';
    const lastName = this.profile.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim() || this.username;
    
    this._cachedComputedProperties.set('fullName', fullName);
    return fullName;
  }

  get displayName(): string {
    return this.profile.firstName || this.username.split('@')[0];
  }

  get initials(): string {
    const cached = this._cachedComputedProperties.get('initials');
    if (cached !== undefined) return cached;

    const firstName = this.profile.firstName || '';
    const lastName = this.profile.lastName || '';
    
    let initials = '';
    if (firstName) initials += firstName[0].toUpperCase();
    if (lastName) initials += lastName[0].toUpperCase();
    
    if (!initials && this.username) {
      initials = this.username[0].toUpperCase();
    }
    
    this._cachedComputedProperties.set('initials', initials);
    return initials;
  }

  get totalBalance(): number {
    const cached = this._cachedComputedProperties.get('totalBalance');
    if (cached !== undefined) return cached;

    const total = Object.values(this.balances).reduce((sum, balance) => sum + balance, 0);
    this._cachedComputedProperties.set('totalBalance', total);
    return total;
  }

  get primaryCurrency(): string {
    // Get the currency with the highest balance
    const currencies = Object.entries(this.balances);
    if (currencies.length === 0) return 'USD';
    
    const [primaryCurrency] = currencies.reduce((max, current) => 
      current[1] > max[1] ? current : max
    );
    
    return primaryCurrency;
  }

  // ===== Role Checking Methods =====

  hasRole(role: string): boolean {
    return this.roles.includes(role);
  }

  hasAnyRole(...roles: string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  hasAllRoles(...roles: string[]): boolean {
    return roles.every(role => this.hasRole(role));
  }

  get isMerchant(): boolean {
    // ONLY check roles array - this is the authoritative source
    return this.hasRole('MERCHANT');
  }

  get isAdmin(): boolean {
    return this._data.hasAdminAccess || this.hasRole('ADMIN');
  }

  get isSupport(): boolean {
    return this._data.isCustomerSupport || this.hasRole('SUPPORT');
  }

  get isDeveloper(): boolean {
    return this.hasRole('DEVELOPER') || !!this.otherProfiles.developer;
  }

  get isBank(): boolean {
    return this.hasRole('BANK') || !!this.otherProfiles.bank;
  }

  // ===== Merchant-specific Methods =====

  get merchantProfile(): MerchantProfile | undefined {
    return this.otherProfiles.merchant;
  }

  get hasMerchantProfile(): boolean {
    return !!this.merchantProfile;
  }

  get isMerchantOnboarded(): boolean {
    const merchant = this.merchantProfile;
    return !!(merchant?.completedOnboarding || merchant?.isPublished);
  }

  get merchantBusinessName(): string | undefined {
    return this.merchantProfile?.businessName;
  }

  // ===== Balance Methods =====

  getBalance(currency: string): number {
    return this.balances[currency] || 0;
  }

  hasBalance(currency: string, amount: number): boolean {
    return this.getBalance(currency) >= amount;
  }

  getCurrencies(): string[] {
    return Object.keys(this.balances);
  }

  hasAnyCurrency(): boolean {
    return this.getCurrencies().length > 0;
  }

  // ===== Profile Completeness =====

  get profileCompleteness(): number {
    let completed = 0;
    let total = 0;

    // Check basic fields
    const checkField = (field: any) => {
      total++;
      if (field) completed++;
    };

    checkField(this.profile.firstName);
    checkField(this.profile.lastName);
    checkField(this.profile.phone);
    checkField(this.email);
    checkField(this.profile.avatar);
    checkField(this.profile.bio);
    checkField(this.profile.dateOfBirth);
    checkField(this.profile.address?.city);
    checkField(this.profile.address?.country);

    return total > 0 ? Math.round((completed / total) * 100) : 0;
  }

  get isProfileComplete(): boolean {
    return this.profileCompleteness >= 80;
  }

  get missingProfileFields(): string[] {
    const missing: string[] = [];
    
    if (!this.profile.firstName) missing.push('First Name');
    if (!this.profile.lastName) missing.push('Last Name');
    if (!this.profile.phone) missing.push('Phone Number');
    if (!this.email) missing.push('Email');
    if (!this.profile.address?.city) missing.push('City');
    if (!this.profile.address?.country) missing.push('Country');
    
    return missing;
  }

  // ===== Validation Methods =====

  canAccessMerchantTools(): boolean {
    return this.isMerchant && this.isMerchantOnboarded;
  }

  canAccessAdminPanel(): boolean {
    return this.isAdmin || this.isSupport;
  }

  canManageUsers(): boolean {
    return this.isAdmin;
  }

  canViewAnalytics(): boolean {
    return this.isAdmin || (this.isMerchant && this.isMerchantOnboarded);
  }

  canCreateTokens(): boolean {
    return !this.isGuest && this.hasAnyCurrency();
  }

  canTransferFunds(): boolean {
    return !this.isGuest && this.totalBalance > 0;
  }

  // ===== Update Methods =====

  updateProfile(updates: Partial<UserProfile>): User {
    const newData = {
      ...this._data,
      profile: {
        ...this._data.profile,
        ...updates
      }
    };
    return new User(newData);
  }

  updateBalances(balances: UserBalances): User {
    const newData = {
      ...this._data,
      balances
    };
    // Clear cached balance-related properties
    this._cachedComputedProperties.delete('totalBalance');
    return new User(newData);
  }

  addRole(role: string): User {
    if (this.hasRole(role)) return this;
    
    const newData = {
      ...this._data,
      roles: [...this.roles, role]
    };
    return new User(newData);
  }

  removeRole(role: string): User {
    if (!this.hasRole(role)) return this;
    
    const newData = {
      ...this._data,
      roles: this.roles.filter(r => r !== role)
    };
    return new User(newData);
  }

  // ===== Serialization =====

  toJSON(): UserData {
    return { ...this._data };
  }

  toPublicJSON(): Partial<UserData> {
    // Return only public-safe fields
    return {
      _id: this._data._id,
      username: this._data.username,
      profile: {
        firstName: this.profile.firstName,
        lastName: this.profile.lastName,
        avatar: this.profile.avatar,
        bio: this.profile.bio
      }
    };
  }

  // ===== Static Factory Methods =====

  static fromJSON(data: UserData): User {
    return new User(data);
  }

  static createGuest(username: string): User {
    return new User({
      _id: `guest_${Date.now()}`,
      username,
      balances: {},
      isGuest: true
    });
  }

  static createEmpty(): User {
    return new User({
      _id: '',
      username: '',
      balances: {}
    });
  }

  // ===== Equality and Comparison =====

  equals(other: User | null): boolean {
    if (!other) return false;
    return this.id === other.id;
  }

  isSameAs(userId: string): boolean {
    return this.id === userId;
  }

  // ===== Debugging =====

  toString(): string {
    return `User(${this.id}, ${this.username}, roles: [${this.roles.join(', ')}])`;
  }
}
